import type { TimeOfDay } from 'generated/openapi';
import { type ReactNode } from 'react';
import { z } from 'zod';

const playScheduleEnum = z.enum([
  'WEEKENDS',
  'MID_WEEK',
  'MORNINGS',
  'EVENINGS',
  'LATE_NIGHT',
]);

import CalendarMidweek from '@/assets/icons/calendar-midweek.svg?react';
import CalendarWeekend from '@/assets/icons/calendar-weekend.svg?react';
import NightWithStars from '@/assets/icons/night-with-stars.svg?react';
import Sunrise from '@/assets/icons/sunrise.svg?react';
import Sunset from '@/assets/icons/sunset.svg?react';

export type PlayScheduleOptionId =
  | 'WEEKENDS'
  | 'MID_WEEK'
  | 'MORNINGS'
  | 'EVENINGS'
  | 'LATE_NIGHT';

export interface PlayScheduleOption {
  id: PlayScheduleOptionId;
  icon: ReactNode;
  label: string;
}

export const PLAY_SCHEDULE_MAP = new Map<
  PlayScheduleOptionId,
  PlayScheduleOption
>([
  [
    'WEEKENDS',
    {
      id: 'WEEKENDS',
      icon: <CalendarWeekend />,
      label: 'Weekends',
    },
  ],
  [
    'MID_WEEK',
    {
      id: 'MID_WEEK',
      icon: <CalendarMidweek />,
      label: 'Mid-week',
    },
  ],
  [
    'MORNINGS',
    {
      id: 'MORNINGS',
      icon: <Sunrise />,
      label: 'Mornings',
    },
  ],
  [
    'EVENINGS',
    {
      id: 'EVENINGS',
      icon: <Sunset />,
      label: 'Evenings',
    },
  ],
  [
    'LATE_NIGHT',
    {
      id: 'LATE_NIGHT',
      icon: <NightWithStars />,
      label: 'Late night',
    },
  ],
]);

export const PLAY_SCHEDULE_OPTIONS: PlayScheduleOption[] = Array.from(
  PLAY_SCHEDULE_MAP.values(),
);

export const playScheduleSchema = z
  .array(playScheduleEnum)
  .min(1, { message: 'Pick at least one slot' });

export function mapPlayScheduleToBackendFormat(
  selections: PlayScheduleOptionId[],
): Record<string, TimeOfDay[]> {
  const hasWeekends = selections.includes('WEEKENDS');
  const hasMidWeek = selections.includes('MID_WEEK');

  // Determine which days to apply
  let days: string[];
  if ((hasWeekends && hasMidWeek) || (!hasWeekends && !hasMidWeek)) {
    // Both selected OR neither selected → all days
    days = [
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
      'SUNDAY',
    ];
  } else if (hasWeekends) {
    // Only WEEKENDS → Sat, Sun
    days = ['SATURDAY', 'SUNDAY'];
  } else {
    // Only MID_WEEK → Mon-Fri
    days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  }

  // Map time periods to TimeOfDay (MORNING, EVENING, NIGHT - AFTERNOON is not used)
  const hasTimePeriods =
    selections.includes('MORNINGS') ||
    selections.includes('EVENINGS') ||
    selections.includes('LATE_NIGHT');
  const hasDaySelection = hasWeekends || hasMidWeek;

  const timeOfDays: TimeOfDay[] = [];
  // If only day selections (WEEKENDS/MID_WEEK) without time periods, fill all 3 time periods
  if (!hasTimePeriods && hasDaySelection) {
    timeOfDays.push('MORNING', 'EVENING', 'NIGHT');
  } else {
    // Otherwise, use selected time periods
    if (selections.includes('MORNINGS')) {
      timeOfDays.push('MORNING');
    }
    if (selections.includes('EVENINGS')) {
      timeOfDays.push('EVENING');
    }
    if (selections.includes('LATE_NIGHT')) {
      timeOfDays.push('NIGHT');
    }
  }

  // Apply times to selected days
  const result: Record<string, TimeOfDay[]> = {};
  for (const day of days) {
    result[day] = [...timeOfDays];
  }

  return result;
}
